import { Clock } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { STATUS_COLOR_CLASS } from '@/styles/statusColors';
import { formatCount } from '@/utils/number';

interface SurveyPeriodStatusProps {
  startDate: string;
  endDate: string;
}

function parseLocalDate(value: string): Date {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year ?? 0, (month ?? 1) - 1, day ?? 1);
}

function resolvePeriod(startDate: string, endDate: string): { label: string; tone: string } {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const start = parseLocalDate(startDate);
  const end = parseLocalDate(endDate);
  const oneDayMs = 24 * 60 * 60 * 1000;
  const daysToStart = Math.round((start.getTime() - today.getTime()) / oneDayMs);
  const daysToEnd = Math.round((end.getTime() - today.getTime()) / oneDayMs);

  if (daysToStart > 0) {
    return {
      label: `Starts in ${formatCount(daysToStart, 'day')}`,
      tone: STATUS_COLOR_CLASS.info,
    };
  }
  if (daysToEnd < 0) {
    return {
      label: `Ended ${formatCount(Math.abs(daysToEnd), 'day')} ago`,
      tone: STATUS_COLOR_CLASS.neutral,
    };
  }
  if (daysToEnd === 0) {
    return { label: 'Ends today', tone: STATUS_COLOR_CLASS.danger };
  }
  if (daysToEnd <= 7) {
    return {
      label: `${formatCount(daysToEnd, 'day')} left`,
      tone: STATUS_COLOR_CLASS.warning,
    };
  }
  return {
    label: `${formatCount(daysToEnd, 'day')} left`,
    tone: STATUS_COLOR_CLASS.success,
  };
}

export function SurveyPeriodStatus({ startDate, endDate }: SurveyPeriodStatusProps) {
  const { label, tone } = resolvePeriod(startDate, endDate);

  return (
    <Badge variant="secondary" className={cn('gap-1', tone)} aria-label={label}>
      <Clock className="size-3" aria-hidden />
      <span>{label}</span>
    </Badge>
  );
}
